import { useState, useEffect, useCallback } from "react";
import { getUserByUsername } from "@/mocks/users.js";
import { useColyseus } from "@/hooks/useColyseus.js";

/**
 * Hook for managing online players state - integrated with WebSocket and mock data
 * Enriches WebSocket data with full user profiles from mocks
 * Falls back to mock data when WebSocket is not available
 * @returns {Object} Online players state and methods
 */
export const useOnlinePlayers = () => {
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const colyseus = useColyseus();
  const wsConnected = !!colyseus?.connected;
  const lobbyState = colyseus?.lobbyState;

  const [usingMockData, setUsingMockData] = useState(false);

  const hasWebSocketConnection = wsConnected;

  const enrichUserData = useCallback((user) => {
    if (!user) return null;

    const usernameStr = String(
      user.username ?? user.name ?? user.id ?? "player"
    );

    const mockUser = usernameStr ? getUserByUsername(usernameStr) : null;

    if (mockUser) {
      return {
        ...mockUser,
        ...user,
        username: usernameStr,
        lastSeen: user.lastSeen || new Date().toISOString(),
        connectedAt: user.connectedAt,
        tables: user.tables || [],
        isOwner: user.isOwner || false,
      };
    }

    return {
      ...user,
      username: usernameStr,
      lastSeen: user.lastSeen || new Date().toISOString(),
      avatarUrl:
        user.avatarUrl ||
        `https://api.dicebear.com/7.x/avataaars/svg?seed=${usernameStr}`,
      email: user.email || `${usernameStr.toLowerCase()}@guildforglory.com`,
      bio: user.bio || "Guild for Glory player",
      status: user.status || "online",
    };
  }, []);

  // Helper: dedupe by username (keep first occurrence)
  const dedupeByUsername = useCallback((list) => {
    const seen = new Set();
    const out = [];
    for (const u of list) {
      const key = (u?.username || "").toLowerCase();
      if (!key) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(u);
    }
    return out;
  }, []);

  useEffect(() => {
    if (wsConnected) {
      const serverPlayers = lobbyState?.onlinePlayers || [];

      const serverUsers = serverPlayers
        .map((user) => enrichUserData(user))
        .filter(Boolean);

      setOnlinePlayers(dedupeByUsername(serverUsers));
      setUsingMockData(false);
    } else {
      setOnlinePlayers([]);
      setUsingMockData(true);
    }
  }, [wsConnected, lobbyState, enrichUserData, dedupeByUsername]);

  const updatePlayerStatus = useCallback((playerId, status) => {
    setOnlinePlayers((prev) =>
      prev.map((player) =>
        player.id === playerId
          ? { ...player, status, lastSeen: new Date().toISOString() }
          : player
      )
    );
  }, []);

  const addOnlinePlayer = useCallback(
    (player) => {
      const enrichedPlayer = enrichUserData(player);

      if (enrichedPlayer) {
        setOnlinePlayers((prev) => {
          if (prev.some((p) => p.id === enrichedPlayer.id)) return prev;
          return [...prev, enrichedPlayer];
        });
      }
    },
    [enrichUserData]
  );

  const removeOnlinePlayer = useCallback((playerId) => {
    setOnlinePlayers((prev) => prev.filter((p) => p.id !== playerId));
  }, []);

  return {
    onlinePlayers,
    updatePlayerStatus,
    addOnlinePlayer,
    removeOnlinePlayer,
    hasWebSocketConnection,
    usingMockData,
  };
};
